import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { getRoleBadgeClass, getRoleDisplayName } from '../lib/utils';
import {
  Building2,
  LayoutDashboard,
  Plus,
  FileText,
  Users,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Upload,
  History,
  Bookmark,
  FileBox,
  FolderTree,
  Settings,
  ClipboardList,
  CalendarDays,
  Wrench,
  BookOpen
} from 'lucide-react';

export const Layout = ({ children }) => {
  const { user, logout, canCreate, isAdmin, canViewSDP, canViewCatalogos } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/catalogo', icon: FileText, label: 'Catálogo' },
    ...(canCreate() ? [{ path: '/criar', icon: Plus, label: 'Criar Código' }] : []),
    ...(canCreate() ? [{ path: '/reservas', icon: Bookmark, label: 'Reservas' }] : []),
    { path: '/desenhos', icon: FileBox, label: 'Desenhos' },
    { path: '/estruturas', icon: FolderTree, label: 'Estruturas' },
    ...(canCreate() ? [{ path: '/importar', icon: Upload, label: 'Importar Códigos' }] : []),
    { path: '/historico', icon: History, label: 'Histórico' },
    // SDP - visible for admin, engenharia, orcamentos, produto
    ...(canViewSDP() ? [{ path: '/sdp', icon: ClipboardList, label: 'SDP' }] : []),
    // SCP - Solicitação de Correção de Projeto - visible for all
    { path: '/scp', icon: Wrench, label: 'SCP' },
    // Reuniões - visible for all authenticated users
    { path: '/reunioes', icon: CalendarDays, label: 'Reuniões' },
    ...(canViewCatalogos() ? [{ path: '/catalogos', icon: BookOpen, label: 'Catálogos e Apresentações' }] : []),
    ...(isAdmin() ? [{ path: '/usuarios', icon: Users, label: 'Usuários' }] : []),
    ...(isAdmin() ? [{ path: '/admin', icon: Settings, label: 'Administração' }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside 
        className={`fixed lg:relative z-40 flex flex-col bg-slate-900 text-white transition-all duration-300 ${
          sidebarOpen ? 'w-60' : 'w-0 lg:w-16'
        } min-h-screen`}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 p-4 border-b border-slate-800 ${!sidebarOpen && 'lg:justify-center'}`}>
          <div className="flex items-center justify-center w-9 h-9 bg-blue-600 rounded-sm flex-shrink-0">
            <Building2 className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          {sidebarOpen && (
            <div className="overflow-hidden">
              <h1 className="font-heading text-sm font-bold tracking-tight whitespace-nowrap">
                GECODE
              </h1>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                Tecnoservice
              </p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.replace('/', '')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              } ${!sidebarOpen && 'lg:justify-center lg:px-2'}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
              {sidebarOpen && <span className="leading-tight">{item.label}</span>}
            </Link>
          ))}
        </nav>

        {/* User section */}
        {sidebarOpen && user && (
          <div className="p-3 border-t border-slate-800">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                <User className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <Badge className={`text-[10px] px-1.5 py-0 ${getRoleBadgeClass(user.role)}`}>
                  {getRoleDisplayName(user.role)}
                </Badge>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-30 glass-header border-b border-slate-200">
          <div className="flex items-center justify-between px-4 lg:px-6 h-14">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex"
            >
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </Button>

            <div className="flex items-center gap-3">
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-600" strokeWidth={1.5} />
                      </div>
                      <span className="hidden md:block text-sm font-medium">{user.name}</span>
                      <ChevronDown className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-red-600 cursor-pointer"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 grid-pattern">
          {children}
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};
